/**
 * Dashboard Page
 * Main landing page after login
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import { useQuery } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format, startOfDay, addDays, startOfWeek } from "date-fns";
import { de } from "date-fns/locale";
import {
  Calendar,
  Video,
  Plus,
  Clock,
  ChevronRight,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
  Button,
  Badge,
  Skeleton,
} from "@jourfx/ui";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: DashboardPage,
});

type DashboardAppointment = {
  _id: string;
  scheduledAt: number;
  duration: number;
  status: string;
  versicherter: { name: string; title?: string | null };
  begutachtungsanlass?: string | null;
  canStartVideo?: boolean;
};

const STATUS_VARIANT: Record<string, "default" | "secondary" | "outline" | "destructive"> = {
  scheduled: "outline",
  confirmed: "default",
  in_progress: "secondary",
  completed: "secondary",
  cancelled: "destructive",
  no_show: "destructive",
};

function getAppointmentStatusLabel(
  t: (key: string) => string,
  status: string
): string {
  switch (status) {
    case "scheduled":
      return t("status.scheduled");
    case "confirmed":
      return t("status.confirmed");
    case "in_progress":
      return t("status.inProgress");
    case "completed":
      return t("status.completed");
    case "cancelled":
      return t("status.cancelled");
    case "no_show":
      return t("status.noShow");
    default:
      return status;
  }
}

function DashboardPage() {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.role === "assistent") {
      navigate({ to: "/calendar" });
    }
  }, [user, navigate]);

  const today = useMemo(() => startOfDay(new Date()).getTime(), []);
  const tomorrow = useMemo(() => addDays(new Date(), 1), []);
  const tomorrowStart = useMemo(() => startOfDay(tomorrow).getTime(), [tomorrow]);
  const weekStart = useMemo(
    () => startOfWeek(new Date(), { weekStartsOn: 1 }).getTime(),
    []
  );

  const todayAppointments = useQuery(
    api.appointments.dashboard.getByGutachterAndDate,
    { date: today }
  ) as DashboardAppointment[] | undefined;

  const tomorrowAppointments = useQuery(
    api.appointments.dashboard.getByGutachterAndDate,
    { date: tomorrowStart }
  ) as DashboardAppointment[] | undefined;

  const stats = useQuery(api.appointments.dashboard.getWeeklyStats, {
    weekStart,
  }) as { total: number; completed: number; pending: number } | undefined;

  const firstName = user?.name?.split(" ")[0];
  const canCreate = user ? ["assistent", "administrator", "super_admin"].includes(user.role) : false;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">
            {firstName ? t("welcome", { name: firstName }) : t("welcomeBack")}
          </h1>
          <p className="text-muted-foreground">
            {format(new Date(), "EEEE, dd. MMMM yyyy", { locale: de })}
          </p>
        </div>
        {canCreate && (
          <Link to="/appointments/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t("newAppointment")}
            </Button>
          </Link>
        )}
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard
          icon={<Calendar className="h-5 w-5 text-primary" />}
          label={t("stats.thisWeek")}
          value={stats ? String(stats.total) : "—"}
        />
        <StatCard
          icon={<Clock className="h-5 w-5 text-muted-foreground" />}
          label={t("stats.completed")}
          value={stats ? String(stats.completed) : "—"}
        />
        <StatCard
          icon={<Video className="h-5 w-5 text-accent-foreground" />}
          label={t("stats.pending")}
          value={stats ? String(stats.pending) : "—"}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Today */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("today.title")}</CardTitle>
            <CardDescription>{t("today.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {todayAppointments === undefined ? (
              <Skeleton className="h-24 w-full" />
            ) : todayAppointments.length === 0 ? (
              <EmptyAppointments
                title={t("today.empty")}
                actionLabel={t("today.openCalendar")}
                actionHref="/calendar"
              />
            ) : (
              todayAppointments.map((apt) => (
                <AppointmentRow
                  key={apt._id}
                  appointment={apt}
                  showStart
                />
              ))
            )}
          </CardContent>
        </Card>

        {/* Tomorrow */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("tomorrow.title")}</CardTitle>
            <CardDescription>{t("tomorrow.subtitle")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {tomorrowAppointments === undefined ? (
              <Skeleton className="h-24 w-full" />
            ) : tomorrowAppointments.length === 0 ? (
              <EmptyAppointments
                title={t("tomorrow.empty")}
                actionLabel={t("today.openCalendar")}
                actionHref="/calendar"
              />
            ) : (
              tomorrowAppointments.map((apt) => (
                <AppointmentRow key={apt._id} appointment={apt} />
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t("quickActions.title")}</CardTitle>
          <CardDescription>{t("quickActions.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {canCreate && (
            <QuickActionCard
              icon={<Plus className="h-6 w-6" />}
              label={t("quickActions.createAppointment")}
              href="/appointments/new"
            />
          )}
          <QuickActionCard
            icon={<Calendar className="h-6 w-6" />}
            label={t("quickActions.openCalendar")}
            href="/calendar"
          />
          <QuickActionCard
            icon={<Clock className="h-6 w-6" />}
            label={t("quickActions.availability")}
            href="/availability"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function AppointmentRow({
  appointment,
  showStart,
}: {
  appointment: DashboardAppointment;
  showStart?: boolean;
}) {
  const { t } = useTranslation("common");
  const { t: tDash } = useTranslation("dashboard");
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const debugMode = testingMode?.enabled ?? false;
  const timeLabel = format(new Date(appointment.scheduledAt), "HH:mm", {
    locale: de,
  });
  return (
    <div className="flex items-center justify-between rounded-md border p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold">{timeLabel}</span>
          <Badge variant={STATUS_VARIANT[appointment.status] ?? "outline"}>
            {getAppointmentStatusLabel(t, appointment.status)}
          </Badge>
        </div>
        <p className="mt-1 text-sm font-medium truncate">
          {appointment.versicherter?.name || "—"}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {appointment.begutachtungsanlass || "—"}
        </p>
      </div>
      <div className="flex items-center gap-2">
        {showStart && (appointment.canStartVideo || debugMode) && (
          <Button size="sm" asChild>
            <Link
              to="/video/$appointmentId"
              params={{ appointmentId: appointment._id }}
            >
              <Video className="mr-2 h-4 w-4" />
              {tDash("startVideo")}
            </Link>
          </Button>
        )}
        <Button variant="ghost" size="icon" asChild>
          <Link to="/appointments/$appointmentId" params={{ appointmentId: appointment._id }}>
            <ChevronRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            {icon}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickActionCard({
  icon,
  label,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  href: string;
}) {
  return (
    <Link
      to={href}
      className="flex items-center gap-3 rounded-lg border p-4 transition-colors hover:bg-accent"
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
        {icon}
      </div>
      <span className="font-medium">{label}</span>
    </Link>
  );
}

function EmptyAppointments({
  title,
  actionLabel,
  actionHref,
}: {
  title: string;
  actionLabel: string;
  actionHref: string;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center text-muted-foreground">
      <p className="text-sm font-medium">{title}</p>
      <Button variant="outline" size="sm" asChild>
        <Link to={actionHref}>{actionLabel}</Link>
      </Button>
    </div>
  );
}
